<template>
  <div class="option-drum">
    <div
      ref="colEl"
      class="option-drum__col"
      @scroll="onScroll"
    >
      <div class="option-drum__spacer" />
      <button
        v-for="opt in normalizedOptions"
        :key="`od-${opt.value}`"
        type="button"
        class="option-drum__cell"
        :class="{ 'is-selected': opt.value === value }"
        @click="selectValue(opt.value)"
      >
        {{ opt.text }}
      </button>
      <div class="option-drum__spacer" />
    </div>
    <div class="option-drum__lines" />
  </div>
</template>

<script>
import {
  computed, nextTick, onMounted, ref, watch,
} from '@vue/composition-api'

const CELL_HEIGHT = 56

// 選択肢リスト型のドラム（ホイール）ピッカー。
// TimeDrumPicker（現在時刻±1時間の5分刻み）と同じ操作感で、
// 営業時間帯全体などの任意の選択肢から選ぶ用途に使う
export default {
  name: 'TimeOptionDrumPicker',
  props: {
    // 選択値（optionsのvalue。nullも可）
    value: {
      type: String,
      default: null,
    },
    // 文字列 or {text, value} の配列
    options: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const colEl = ref(null)
    let scrollTimer = null
    let suppressScroll = false

    const normalizedOptions = computed(() => props.options.map(opt => (
      typeof opt === 'string' ? { text: opt, value: opt } : opt
    )))

    const indexOfValue = value => {
      const index = normalizedOptions.value.findIndex(opt => opt.value === value)

      return index >= 0 ? index : 0
    }

    const scrollToValue = (value, smooth = false) => {
      if (!colEl.value) return
      suppressScroll = true
      colEl.value.scrollTo({ top: indexOfValue(value) * CELL_HEIGHT, behavior: smooth ? 'smooth' : 'auto' })
      setTimeout(() => {
        suppressScroll = false
      }, smooth ? 350 : 50)
    }

    const onScroll = () => {
      if (suppressScroll || !colEl.value) return
      if (scrollTimer) clearTimeout(scrollTimer)
      scrollTimer = setTimeout(() => {
        if (!colEl.value) return
        const index = Math.round(colEl.value.scrollTop / CELL_HEIGHT)
        const clamped = Math.min(Math.max(index, 0), normalizedOptions.value.length - 1)
        const opt = normalizedOptions.value[clamped]
        if (opt && opt.value !== props.value) emit('input', opt.value)
        scrollToValue(opt?.value, true)
      }, 120)
    }

    const selectValue = value => {
      emit('input', value)
      scrollToValue(value, true)
    }

    onMounted(async () => {
      await nextTick()
      scrollToValue(props.value)
    })

    watch(() => props.value, value => {
      if (!colEl.value) return
      const current = Math.round(colEl.value.scrollTop / CELL_HEIGHT)
      if (current !== indexOfValue(value)) scrollToValue(value, true)
    })

    return {
      colEl,
      normalizedOptions,
      onScroll,
      selectValue,
    }
  },
}
</script>

<style scoped>
.option-drum {
  position: relative;
  height: 224px; /* 4セル分（中央1 + 上下1.5） */
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 10px;
}

.option-drum__col {
  height: 100%;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  /* 上下をフェード（背景色に依存しないようマスクで表現） */
  -webkit-mask-image: linear-gradient(transparent 0%, #000 28%, #000 72%, transparent 100%);
  mask-image: linear-gradient(transparent 0%, #000 28%, #000 72%, transparent 100%);
}

.option-drum__col::-webkit-scrollbar {
  display: none;
}

.option-drum__spacer {
  height: 84px; /* (224 - 56) / 2 */
}

.option-drum__cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 56px;
  scroll-snap-align: center;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-weight: 500;
  font-size: 19px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.option-drum__cell.is-selected {
  color: #9155fd;
  font-weight: 700;
  font-size: 23px;
}

.option-drum__lines {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.option-drum__lines::before,
.option-drum__lines::after {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  height: 1px;
  background: rgba(145, 85, 253, 0.45);
}

.option-drum__lines::before {
  top: 84px;
}

.option-drum__lines::after {
  bottom: 84px;
}
</style>
